import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApiDocsAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, MagnifyingGlass, Lock, Globe, ShieldCheck,
  CurrencyDollar, Users, Storefront, Receipt, Bell, ChartBar,
  ChatCircleDots, Tag, UploadSimple, QrCode, Link as LinkIcon,
  Code, GearSix, Heart, Rss, Envelope
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

const CATEGORY_ICONS = {
  'Authentication': Lock,
  'Users': Users,
  'Products & Services': Storefront,
  'Transactions': CurrencyDollar,
  'Payment Requests': Receipt,
  'Wallet & Payments': CurrencyDollar,
  'Social (Follows & Feed)': Rss,
  'Favorites': Heart,
  'QR Codes & Scan Tracking': QrCode,
  'Payment Links & Embeds': LinkIcon,
  'Receipts & Invoices': Receipt,
  'Notifications': Bell,
  'Seller Analytics': ChartBar,
  'CRM (Vendor)': ChatCircleDots,
  'Buyer Messages': ChatCircleDots,
  'Uploads': UploadSimple,
  'Platform Fees': CurrencyDollar,
  'Admin - Fee Management': GearSix,
  'Admin - User Management': ShieldCheck,
  'Admin - Domain Verification': Envelope,
  'System': Globe,
};

const METHOD_COLORS = {
  GET: { bg: 'bg-[#22C55E]/10', text: 'text-[#22C55E]' },
  POST: { bg: 'bg-[#22C55E]/10', text: 'text-[#22C55E]' },
  PUT: { bg: 'bg-[#F59E0B]/10', text: 'text-[#F59E0B]' },
  DELETE: { bg: 'bg-[#EF4444]/10', text: 'text-[#EF4444]' },
};

export default function AdminAPIPage() {
  const [catalog, setCatalog] = useState(null);
  const [search, setSearch] = useState('');
  const [expandedCat, setExpandedCat] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCatalog(); }, []);

  const loadCatalog = async () => {
    try {
      const { data } = await adminApiDocsAPI.getCatalog();
      setCatalog(data);
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };

  const filteredCategories = catalog?.categories?.filter(cat => {
    if (!search) return true;
    const q = search.toLowerCase();
    return cat.category.toLowerCase().includes(q) ||
      cat.description.toLowerCase().includes(q) ||
      cat.endpoints.some(e => e.path.toLowerCase().includes(q) || e.description.toLowerCase().includes(q));
  }) || [];

  const toggleCat = (catName) => {
    setExpandedCat(prev => prev === catName ? null : catName);
  };

  return (
    <div className="min-h-screen bg-[#111111]" data-testid="admin-api-page">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard"><img src={LOGO_URL} alt="Divispend" className="h-10 w-auto" /></Link>
            <span className="text-[#666666]">/</span>
            <span className="text-[#22C55E] font-semibold text-sm">Admin</span>
            <span className="text-[#666666]">/</span>
            <span className="text-white font-semibold text-sm">API Reference</span>
          </div>
          <Link to="/dashboard">
            <Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm text-sm">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {loading ? (
          <div className="text-center py-20 text-[#666666]">Loading API catalog...</div>
        ) : !catalog ? (
          <div className="text-center py-20 text-[#666666]">Failed to load API catalog.</div>
        ) : (
          <>
            {/* Stats */}
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <Code className="h-5 w-5 text-[#22C55E]" />
                <span className="text-white text-sm font-semibold">{catalog.total_endpoints} Endpoints</span>
              </div>
              <div className="flex items-center gap-2">
                <Tag className="h-5 w-5 text-[#22C55E]" />
                <span className="text-white text-sm font-semibold">{catalog.total_categories} Categories</span>
              </div>
            </div>

            {/* Search */}
            <div className="relative mb-6">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666666]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search endpoints, paths, or descriptions..."
                className="pl-9 bg-[#1A1A1A] border-[#333333] text-white rounded-lg h-11"
                data-testid="api-search"
              />
            </div>

            {/* Categories */}
            <div className="space-y-3">
              {filteredCategories.map(cat => {
                const CatIcon = CATEGORY_ICONS[cat.category] || Globe;
                const isExpanded = expandedCat === cat.category || !!search;
                const endpoints = search
                  ? cat.endpoints.filter(e => {
                      const q = search.toLowerCase();
                      return e.path.toLowerCase().includes(q) || e.description.toLowerCase().includes(q) || e.method.toLowerCase().includes(q);
                    })
                  : cat.endpoints;

                if (search && endpoints.length === 0) return null;

                return (
                  <Card key={cat.category} className="bg-[#1A1A1A] border-[#222222] rounded-lg overflow-hidden" data-testid={`api-cat-${cat.category}`}>
                    <div
                      onClick={() => toggleCat(cat.category)}
                      className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-[#151515] transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <CatIcon className="h-5 w-5 text-[#22C55E]" weight="duotone" />
                        <div>
                          <p className="text-sm font-semibold text-white">{cat.category}</p>
                          <p className="text-xs text-[#666666]">{cat.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold bg-[#333333] text-[#999999] px-2 py-0.5 rounded">{endpoints.length} endpoint{endpoints.length !== 1 ? 's' : ''}</span>
                        <span className={`text-[#666666] text-xs transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="border-t border-[#222222]">
                        {endpoints.map((ep) => {
                          const mc = METHOD_COLORS[ep.method] || METHOD_COLORS.GET;
                          return (
                            <div key={`${ep.method}-${ep.path}`} className="px-4 py-2.5 flex items-start gap-3 border-b border-[#151515] last:border-0 hover:bg-[#111111] transition-colors" data-testid={`api-endpoint-${ep.method}-${ep.path}`}>
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded shrink-0 mt-0.5 ${mc.bg} ${mc.text}`}>
                                {ep.method}
                              </span>
                              <div className="flex-1 min-w-0">
                                <code className="text-xs text-white font-mono">{ep.path}</code>
                                <p className="text-xs text-[#666666] mt-0.5">{ep.description}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
